import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView, Text } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Warning, BookOpen } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import NewCustomAxioActionModal from "../../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";

const modals = {
  deleteConfirmation: {
    title: "Success!",
    ariaLabel: "Degree Deleted Successfully Modal",
    warningText: "The degree has been successfully deleted.",
    actionButtonText: "OK",
  },
  deleteError: {
    title: "Error!",
    ariaLabel: "Degree Delete Error Modal",
    warningText: "An error occurred while deleting the degree. You may not have permission to delete a degree.",
    actionButtonText: "OK",
  },
};

export enum DeleteDegreeModalState {
  CLOSED = "CLOSED",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  DELETE_ERROR = "DELETE_ERROR",
}

const deleteConfirmationSchema = z.object({
  confirmation: z.string().refine((val) => val === "DELETE" || val === "", {
    message: "Please type DELETE to confirm",
  }),
});

type DeleteConfirmationFormData = z.infer<typeof deleteConfirmationSchema>;

interface DeleteDegreeModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: DeleteConfirmationFormData) => Promise<any>;
  degreeName: string;
  enrolledStudentsCount: number;
}

const DeleteDegreeModal: React.FC<DeleteDegreeModalProps> = ({
  visible,
  onClose,
  onSubmit,
  degreeName,
  enrolledStudentsCount,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const [modalState, setModalState] = useState({
    state: DeleteDegreeModalState.CLOSED,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    watch,
  } = useForm<DeleteConfirmationFormData>({
    resolver: zodResolver(deleteConfirmationSchema),
    defaultValues: {
      confirmation: "",
    },
  });

  const confirmationValue = watch("confirmation");
  const isDeleteTyped = confirmationValue === "DELETE";

  // Reset form when modal becomes visible
  useEffect(() => {
    if (visible) {
      reset({ confirmation: "" });
    }
  }, [visible, reset]);

  const handleClose = () => {
    reset();
    setModalState({ state: DeleteDegreeModalState.CLOSED });
    onClose();
  };

  const handleSubmitForm = async (data: DeleteConfirmationFormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(data);

      if (result?.deleteDegree?.success) {
        reset();
        setModalState({ state: DeleteDegreeModalState.DELETE_CONFIRMATION });
        onClose();
      } else {
        setModalState({ state: DeleteDegreeModalState.DELETE_ERROR });
      }
    } catch (error) {
      setModalState({ state: DeleteDegreeModalState.DELETE_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomModal
        visible={visible}
        onRequestClose={handleClose}
        title="Delete Degree Program"
        subtitle="Permanently remove this degree program"
        showCloseButton={true}
        exitFill="#6B7280"
        onShow={() => {}}
        ariaLabel="Delete degree modal">
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
          <View style={styles.container}>
            {/* Primary Confirmation */}
            <View style={styles.confirmationSection}>
              <CustomText
                text="Are you absolutely sure?"
                size="l"
                weight="bold"
                style={{ color: "#111827", marginBottom: 8 }}
              />
              <Text style={{ color: "#374151", textAlign: "center", lineHeight: 20, fontSize: 14 }}>
                {t("text.delete_degree_confirmation_prefix")}{" "}
                <Text style={{ color: "#DC2626", fontWeight: "bold" }}>{degreeName}</Text>
                {t("text.delete_degree_confirmation_suffix")}
              </Text>
            </View>

            {/* Enrolled Students Count */}
            <View style={styles.studentsCountBox}>
              <CustomText
                text="enrolled_students"
                interpolation={{ enrolledStudentsCount }}
                size="xl"
                weight="bold"
                style={{ color: "#111827" }}
              />
              <CustomText
                text="Currently enrolled in this degree program"
                size="s"
                weight="normal"
                style={{ color: "#6B7280" }}
              />
            </View>

            {/* Important Note */}
            <View style={styles.importantNoteBox}>
              <View style={styles.noteHeader}>
                <BookOpen size={16} color="#3B82F6" />
                <CustomText text="Important Note" size="s" weight="bold" style={{ color: "#3B82F6", marginLeft: 8 }} />
              </View>
              <CustomText
                text="Courses will NOT be deleted. All courses currently assigned to this degree will remain in the system and can be reassigned to other degree programs."
                size="s"
                weight="normal"
                style={{ color: "#374151", marginTop: 8 }}
              />
            </View>

            {/* Permanent Deletion Details */}
            <View style={styles.deletionDetailsBox}>
              <View style={styles.noteHeader}>
                <Warning size={16} color="#F59E0B" />
                <CustomText
                  text="This will permanently delete"
                  size="s"
                  weight="bold"
                  style={{ color: "#F59E0B", marginLeft: 8 }}
                />
              </View>
              <View style={styles.deletionList}>
                <View style={styles.listItem}>
                  <Text>• </Text>
                  <CustomText text="The degree program structure and requirements" size="s" weight="normal" />
                </View>
                <View style={styles.listItem}>
                  <Text>• </Text>
                  <CustomText text="Student enrollment records for this degree" size="s" weight="normal" />
                </View>
                <View style={styles.listItem}>
                  <Text>• </Text>
                  <CustomText text="Academic progress tracking and transcripts" size="s" weight="normal" />
                </View>
                <View style={styles.listItem}>
                  <Text>• </Text>
                  <CustomText text="All reports and analytics for this degree" size="s" weight="normal" />
                </View>
              </View>
            </View>

            {/* Confirmation Input */}
            <View style={styles.typeSection}>
              <Text style={{ color: "#344054", fontSize: 14, fontWeight: "500", marginBottom: 8 }}>
                {t("text.Type")} <Text style={{ color: "#DC2626", fontWeight: "bold" }}>{t("text.DELETE")}</Text>
                {t("text.to_confirm")}
              </Text>
              <Controller
                control={control}
                name="confirmation"
                render={({ field: { onChange, value } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Type DELETE here"
                    style={styles.input}
                  />
                )}
              />
              {errors?.confirmation?.message && (
                <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.confirmation?.message} />
              )}
            </View>

            {/* Action Buttons */}
            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleClose}
                styleType="transparent"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
              />
              <CustomButton
                text="Delete Degree"
                onPress={handleSubmit(handleSubmitForm)}
                styleType="danger"
                style={styles.deleteButton}
                textStyle={styles.deleteButtonText}
                disabled={!isDeleteTyped || isSubmitting}
              />
            </View>
          </View>
        </ScrollView>
      </CustomModal>
      <NewCustomAxioActionModal
        visible={modalState.state === DeleteDegreeModalState.DELETE_CONFIRMATION}
        onClose={() => setModalState({ state: DeleteDegreeModalState.CLOSED })}
        modalTexts={modals.deleteConfirmation}
        contextualText={degreeName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === DeleteDegreeModalState.DELETE_ERROR}
        onClose={() => setModalState({ state: DeleteDegreeModalState.CLOSED })}
        modalTexts={modals.deleteError}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  confirmationSection: {
    marginBottom: 24,
    alignItems: "center",
  },
  studentsCountBox: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    alignItems: "center",
  },
  importantNoteBox: {
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
    backgroundColor: "#F0F9FF",
    padding: 16,
    marginBottom: 16,
    borderRadius: 4,
  },
  deletionDetailsBox: {
    borderLeftWidth: 4,
    borderLeftColor: "#DC2626",
    backgroundColor: "#FEF2F2",
    padding: 16,
    marginBottom: 24,
    borderRadius: 4,
  },
  noteHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  deletionList: {
    marginTop: 8,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
    color: "#374151",
  },
  input: {
    width: "100%",
  },
  typeSection: {
    marginBottom: 24,
    alignItems: "flex-start",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  deleteButton: {
    backgroundColor: "#DC2626",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  deleteButtonText: {
    color: "#FFFFFF",
  },
});

export default DeleteDegreeModal;
